// ── Material Kit ──
// Per-job kit derived from the bound JobSpec BOM. PM/Ops verifies each line
// before the install phase can proceed. If the kit cannot be fully confirmed,
// a PM override (reason + notes) is required to unlock install.
//
// Scope (locked):
//  • No BoxHero/inventory sync, no PO automation, no vendor workflows.
//  • No substitutions — items match SKUs in the bound JobSpec BOM exactly.
//  • Confirmation is a manual operator action; this is the audit surface,
//    not the procurement system.

import type { BoundJobSpec, JobSpec, JobSpecBomItem } from "./jobSpec";
import { resolveBoundSpec } from "./jobSpec";

export type KitItemStatus = "pending" | "confirmed" | "missing";

export interface MaterialKitItem {
  sku: string;
  name: string;
  requiredQuantity: number;
  unit?: string;
  status: KitItemStatus;
  /** Set when an operator marks the item confirmed or missing. */
  decidedAt?: string;
  decidedBy?: string;
}

export type KitOverrideReason =
  | "partial_substitute_on_truck"
  | "non_critical_missing"
  | "delivery_in_transit"
  | "customer_supplied"
  | "other";

export const KIT_OVERRIDE_REASONS: { id: KitOverrideReason; label: string }[] = [
  { id: "partial_substitute_on_truck", label: "Approved substitute already on truck" },
  { id: "non_critical_missing", label: "Missing item is non-critical to start" },
  { id: "delivery_in_transit", label: "Delivery confirmed in transit" },
  { id: "customer_supplied", label: "Customer supplying item" },
  { id: "other", label: "Other" },
];

export interface KitOverride {
  reason: KitOverrideReason;
  notes: string;
  decidedAt: string;
  decidedBy: string;
}

export interface MaterialKit {
  jobId: string;
  specRef: { specId: string; versionNumber: number };
  items: MaterialKitItem[];
  /** PM override — only honored if all items aren't confirmed. */
  override?: KitOverride;
}

// ── Factory / lookups ─────────────────────────────────────────────────────

export function createKitFromSpec(jobId: string, spec: JobSpec): MaterialKit {
  return {
    jobId,
    specRef: { specId: spec.specId, versionNumber: spec.versionNumber },
    items: spec.billOfMaterials.map(toKitItem),
  };
}

function toKitItem(b: JobSpecBomItem): MaterialKitItem {
  return {
    sku: b.sku,
    name: b.name,
    requiredQuantity: b.quantity,
    unit: b.unit,
    status: "pending",
  };
}

export function getKit(kits: MaterialKit[], jobId: string): MaterialKit | undefined {
  return kits.find((k) => k.jobId === jobId);
}

export function ensureKit(
  kits: MaterialKit[],
  jobId: string,
  bound: BoundJobSpec,
): { kits: MaterialKit[]; kit: MaterialKit } {
  const existing = getKit(kits, jobId);
  if (existing) return { kits, kit: existing };
  // Only pinned snapshots can generate a kit. Legacy bindings without a
  // snapshot must not fall back to the live canonical registry.
  const spec = resolveBoundSpec(bound);
  if (!spec) throw new Error(`Cannot ensure kit: spec ${bound.specId}-v${bound.versionNumber} not found`);
  const fresh = createKitFromSpec(jobId, spec);
  return { kits: [...kits, fresh], kit: fresh };
}

// ── Gating ────────────────────────────────────────────────────────────────

export function isKitFullyConfirmed(kit: MaterialKit): boolean {
  if (kit.items.length === 0) return true;
  return kit.items.every((i) => i.status === "confirmed");
}

/**
 * SINGLE PREDICATE for kit-gate enforcement.
 *
 * A kit "clears" when it exists AND (every item is confirmed OR a PM override
 * is logged). Returns false for `undefined` so callers must always pass the
 * resolved kit (or explicitly reflect "kit not generated" in their UI).
 *
 * Used by:
 *   • dispatch / assignment guards (kitDispatchGuard.assertJobKitReady)
 *   • install phase unlock (jobSpecExecution.isPhaseUnlocked)
 *   • install phase submit (jobSpecExecution.submitPhase)
 *
 * Do not duplicate this logic inline anywhere.
 */
export function isKitGateCleared(kit: MaterialKit | undefined): boolean {
  if (!kit) return false;
  return isKitFullyConfirmed(kit) || !!kit.override;
}

/** @deprecated Use `isKitGateCleared`. Retained as alias to avoid churn. */
export const isInstallKitGateCleared = isKitGateCleared;

export function kitProgress(kit: MaterialKit): {
  confirmed: number;
  missing: number;
  pending: number;
  total: number;
  pct: number;
} {
  const total = kit.items.length;
  const confirmed = kit.items.filter((i) => i.status === "confirmed").length;
  const missing = kit.items.filter((i) => i.status === "missing").length;
  const pending = total - confirmed - missing;
  const pct = total === 0 ? 100 : Math.round((confirmed / total) * 100);
  return { confirmed, missing, pending, total, pct };
}

// ── State transitions (pure) ──────────────────────────────────────────────

function updateItem(
  kit: MaterialKit,
  sku: string,
  patch: (i: MaterialKitItem) => MaterialKitItem,
): MaterialKit {
  return { ...kit, items: kit.items.map((i) => (i.sku === sku ? patch(i) : i)) };
}

export type KitResult =
  | { ok: true; kit: MaterialKit }
  | { ok: false; reason: string };

export function setKitItemStatus(
  kit: MaterialKit,
  sku: string,
  status: KitItemStatus,
  decidedBy: string,
  now: Date = new Date(),
): KitResult {
  if (!kit.items.some((i) => i.sku === sku)) {
    return { ok: false, reason: `SKU ${sku} not in kit.` };
  }
  if (status !== "pending" && !decidedBy.trim()) {
    return { ok: false, reason: "Operator name is required to confirm or flag an item." };
  }
  const stamp = status === "pending" ? undefined : now.toISOString();
  const by = status === "pending" ? undefined : decidedBy.trim();
  const next = updateItem(kit, sku, (i) => ({
    ...i,
    status,
    decidedAt: stamp,
    decidedBy: by,
  }));
  // Confirming all items invalidates a stale override (kit is clean now).
  const cleaned =
    isKitFullyConfirmed(next) && next.override ? { ...next, override: undefined } : next;
  return { ok: true, kit: cleaned };
}

const OVERRIDE_NOTES_MIN = 20;

export interface OverrideInput {
  reason: KitOverrideReason;
  notes: string;
  decidedBy: string;
}

export function logKitOverride(
  kit: MaterialKit,
  input: OverrideInput,
  now: Date = new Date(),
): KitResult {
  if (isKitFullyConfirmed(kit)) {
    return { ok: false, reason: "Kit is already fully confirmed — no override needed." };
  }
  if (!input.decidedBy.trim()) return { ok: false, reason: "PM name is required." };
  const notes = input.notes.trim();
  if (!notes) return { ok: false, reason: "Override notes are required." };
  if (notes.length < OVERRIDE_NOTES_MIN) {
    return {
      ok: false,
      reason: `Override notes must include meaningful detail (min ${OVERRIDE_NOTES_MIN} characters).`,
    };
  }
  const override: KitOverride = {
    reason: input.reason,
    notes,
    decidedAt: now.toISOString(),
    decidedBy: input.decidedBy.trim(),
  };
  return { ok: true, kit: { ...kit, override } };
}

export function clearKitOverride(kit: MaterialKit): MaterialKit {
  if (!kit.override) return kit;
  return { ...kit, override: undefined };
}
